import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import BusinessMpProvider from "@/core/data-provider/data-mp/business/business-mp-provider";
import useAppDispatch from "@/core/helpers/useAppDispatch";
import useAppSelector from "@/core/helpers/useAppSelector";
import { RpcMpProviderSuccessResponse } from "@/core/rpc-mp-provider/responses/rpc-mp-provider-success-response";
import { DeviceEnum } from "@/domains/business/constants/deviceEnum";
import { infoSelector, pcInfoSelector, setInfo } from "@/domains/business/slice";
import toast from "@/domains/toasts/helpers/toast";

interface Props {
  setIsShow: (v: boolean) => void;
  device: DeviceEnum;
  onSuccessSell: () => void;
}

export default function useBusinessSell({ setIsShow, device, onSuccessSell }: Props) {
  const { t, i18n } = useTranslation("business");
  const dispatch = useAppDispatch();

  const [price, setPrice] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isSelling, setIsSelling] = useState(false);

  const tabletInfo = useAppSelector(infoSelector);
  const pcInfo = useAppSelector(pcInfoSelector);

  const business = device === DeviceEnum?.PC ? pcInfo : tabletInfo;

  const load = useCallback(async () => {
    if (!business?.id) return;
    setIsLoading(true);

    try {
      const response = await BusinessMpProvider.getSellPrice(business.id);

      if (response instanceof RpcMpProviderSuccessResponse) {
        setPrice(response.data);
      } else if (
        response?.error?.message &&
        (i18n.exists(`business:business.errors.${response.error.message}`) ||
          i18n.exists(`business.errors.${response.error.message}`))
      ) {
        console.error(response.error.message);
        toast.error(t(`business.errors.${response.error.message}`));
        setIsShow(false);
      } else {
        console.error(response?.error?.message ?? "Unknown RPC error");
        toast.error(t("business.errors.sellLoadError"));
        setIsShow(false);
      }
    } catch (e) {
      console.error(e);
      toast.error(t("business.errors.sellLoadError"));
      setIsShow(false);
    } finally {
      setIsLoading(false);
    }
  }, [business?.id, i18n, setIsShow, t]);

  useEffect(() => {
    load();
  }, [load]);

  const sell = useCallback(async () => {
    if (!business?.id) return;
    setIsSelling(true);

    try {
      const response = await BusinessMpProvider.sellBusiness(business.id);

      if (response instanceof RpcMpProviderSuccessResponse) {
        toast.success(t("business.successSell"));
        setIsShow(false);
        dispatch(setInfo(null));
        if (device === DeviceEnum.TABLET) onSuccessSell();
      } else if (
        response?.error?.message &&
        (i18n.exists(`business:business.errors.${response.error.message}`) ||
          i18n.exists(`business.errors.${response.error.message}`))
      ) {
        console.error(response.error.message);
        toast.error(t(`business.errors.${response.error.message}`));
        setIsShow(false);
      } else {
        console.error(response?.error?.message ?? "Unknown RPC error");
        toast.error(t("business.errors.sellError"));
        setIsShow(false);
      }
    } catch (e) {
      console.error(e);
      toast.error(t("business.errors.sellError"));
      setIsShow(false);
    } finally {
      setIsSelling(false);
    }
  }, [business?.id, device, dispatch, i18n, onSuccessSell, setIsShow, t]);

  return {
    business,
    price,
    isLoading,
    sell,
    isSelling,
  };
}
